/**
 * Modulo Vuex per il tracciamento degli appuntamenti Rolex
 *
 * Questo modulo integra il tracciamento Rolex con lo store Vuex,
 * fornendo actions e mutations per gestire il tracciamento.
 */

import rolexTracking from '../../utils/rolexTracking';

// State iniziale
const state = {
  trackingEnabled: false,
  consentGiven: false,
  rolexContext: false,
  lastTrackedEvent: null,
  isReady: false
};

// Getters
const getters = {
  isTrackingEnabled: state => state.trackingEnabled,
  getLastTrackedEvent: state => state.lastTrackedEvent,
  isInRolexContext: state => state.rolexContext,
  isTrackingReady: state => state.isReady
};

// Mutations
const mutations = {
  SET_TRACKING_ENABLED(state, value) {
    state.trackingEnabled = value;
  },
  SET_CONSENT_GIVEN(state, value) {
    state.consentGiven = value;
  },
  SET_ROLEX_CONTEXT(state, value) {
    state.rolexContext = value;
  },
  SET_LAST_TRACKED_EVENT(state, event) {
    state.lastTrackedEvent = event;
  },
  SET_TRACKING_READY(state, value) {
    state.isReady = value;
  }
};

// Actions
const actions = {
  /**
   * Inizializza il tracciamento
   */
  initTracking({ commit, dispatch }) {
    // rolexTracking si inizializza automaticamente al caricamento
    // Ascolta l'evento di ready
    window.addEventListener('rolexTrackingReady', event => {
      commit('SET_CONSENT_GIVEN', event.detail.consent);
      commit('SET_ROLEX_CONTEXT', event.detail.rolex);
      commit('SET_TRACKING_ENABLED', event.detail.canTrack);
      commit('SET_TRACKING_READY', true);

      console.log('Vuex RolexTracking: Tracciamento ready =', event.detail);
    });

    // Controlla se il modulo di tracciamento è già pronto
    if (rolexTracking.isInitialized) {
      dispatch('checkTrackingStatus');
    }
  },

  /**
   * Controlla lo stato del tracciamento
   */
  checkTrackingStatus({ commit }) {
    const status = rolexTracking.getStatus();

    commit('SET_CONSENT_GIVEN', status.trackingConsent);
    commit('SET_ROLEX_CONTEXT', status.isRolexPage);
    commit('SET_TRACKING_ENABLED', status.isRolexPage && status.trackingConsent);
    commit('SET_TRACKING_READY', rolexTracking.isInitialized);

    return status;
  },

  /**
   * Forza un aggiornamento del consenso
   */
  refreshConsent({ commit }) {
    const consent = rolexTracking.refreshConsent();
    commit('SET_CONSENT_GIVEN', consent);
    commit('SET_TRACKING_ENABLED', state.rolexContext && consent);

    return consent;
  },

  /**
   * Traccia la prenotazione di un appuntamento
   */
  trackBookAppointment({ commit, state }, appointmentData) {
    if (!state.trackingEnabled) {
      console.log('Vuex RolexTracking: Tracciamento disabilitato, impossibile tracciare bookAppointment');
      return false;
    }

    const success = rolexTracking.trackBookAppointment(appointmentData);

    if (success) {
      commit('SET_LAST_TRACKED_EVENT', {
        type: 'bookAppointment',
        data: appointmentData,
        timestamp: new Date().toISOString()
      });
    }

    return success;
  },

  /**
   * Traccia la modifica di un appuntamento
   */
  trackModifyAppointment({ commit, state }, appointmentData) {
    if (!state.trackingEnabled) {
      console.log('Vuex RolexTracking: Tracciamento disabilitato, impossibile tracciare modifyAppointment');
      return false;
    }

    const success = rolexTracking.trackModifyAppointment(appointmentData);

    if (success) {
      commit('SET_LAST_TRACKED_EVENT', {
        type: 'modifyAppointment',
        data: appointmentData,
        timestamp: new Date().toISOString()
      });
    }

    return success;
  },

  /**
   * Traccia la prenotazione di un nuovo appuntamento dopo cancellazione
   */
  trackNewAppointment({ commit, state }) {
    if (!state.trackingEnabled) {
      console.log('Vuex RolexTracking: Tracciamento disabilitato, impossibile tracciare newAppointment');
      return false;
    }

    const success = rolexTracking.trackNewAppointment();

    if (success) {
      commit('SET_LAST_TRACKED_EVENT', {
        type: 'newAppointment',
        timestamp: new Date().toISOString()
      });
    }

    return success;
  },

  /**
   * Traccia la cancellazione di un appuntamento
   */
  trackCancelAppointment({ commit, state }) {
    if (!state.trackingEnabled) {
      console.log('Vuex RolexTracking: Tracciamento disabilitato, impossibile tracciare cancelAppointment');
      return false;
    }

    const success = rolexTracking.trackCancelAppointment();

    if (success) {
      commit('SET_LAST_TRACKED_EVENT', {
        type: 'cancelAppointment',
        timestamp: new Date().toISOString()
      });
    }

    return success;
  },

  /**
   * Registra plugin globale Vue per il tracciamento
   * @param {Object} vue - Istanza Vue
   */
  registerVuePlugin({ state }, vue) {
    if (!vue) {
      console.error('Vuex RolexTracking: Impossibile registrare il plugin Vue, istanza Vue non fornita');
      return;
    }

    // Rendi rolexTracking disponibile globalmente come $rolexTracking
    vue.prototype.$rolexTracking = rolexTracking;

    console.log('Vuex RolexTracking: Plugin Vue registrato con successo');
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};